import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import SubtabNav from '../Workflow/SubtabNav'
import S from './WorkflowMobileSheet.module.scss'

const NAV_H = 52

// Detents as fractions of the container height (= sheet's `top` value).
// Smaller top = taller sheet.
const DETENTS = [0.82, 0.44, 0.14]
// 0.82 → peek:     chart dominant, grabber + tab bar just visible
// 0.44 → mid:      balanced working view (auto-raised on first interaction)
// 0.14 → expanded: controls focus, chart peeks above

function getDetentTops(containerH) {
  return DETENTS.map((d) => Math.round(containerH * d))
}

// Find where the workflow content starts by querying the sticky progress strip.
// Falls back to CSS variables if the strip is not yet in the DOM.
function measureContentTop() {
  if (typeof window === 'undefined') return 0
  const strip = document.querySelector('.workflow-progress-strip')
  if (strip) return Math.round(strip.getBoundingClientRect().bottom)
  const headerH =
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height'
      )
    ) || 50
  return headerH
}

/**
 * Generic mobile bottom-sheet layout used by Map, Style, and Export steps.
 *
 * Props:
 *   mainContent   — JSX pinned as background (chart / export preview)
 *   topStrip      — optional JSX in a compact horizontal strip above the preview
 *   tabs          — optional tab config; if provided renders SubtabNav in sheet header
 *   activeTab     — active tab id
 *   onTabChange   — tab change handler (auto-raise is wired internally)
 *   sheetContent  — JSX scrolled inside the sheet body
 *   actions       — JSX pinned in the nav bar (Back / Next buttons)
 *   tabsOverride  — when provided (including null), replaces the tab bar with this
 *                   content; use null to hide tabs with no replacement
 */
export default function WorkflowMobileSheet({
  mainContent,
  topStrip = null,
  tabs = null,
  activeTab,
  onTabChange,
  sheetContent,
  actions = null,
  showFullscreenBtn = true,
  tabsOverride = undefined,
}) {
  const sheetRef = useRef(null)
  const topOffsetRef = useRef(0)
  const actionsRef = useRef(actions)
  actionsRef.current = actions
  const [topOffset, setTopOffset] = useState(null)

  const detentIndexRef = useRef(1)
  const [sheetTop, setSheetTop] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const currentTopRef = useRef(0)
  const draggingRef = useRef(false)
  const startYRef = useRef(0)
  const startTopRef = useRef(0)
  const movedRef = useRef(0)

  const [previewFullscreen, setPreviewFullscreen] = useState(false)

  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false,
    []
  )

  const getContainerH = useCallback(
    () => window.innerHeight - topOffsetRef.current,
    []
  )

  const snapToIndex = useCallback(
    (i, animate = true) => {
      const idx = Math.max(0, Math.min(DETENTS.length - 1, i))
      const tops = getDetentTops(getContainerH())
      const top = tops[idx]
      detentIndexRef.current = idx
      currentTopRef.current = top
      setSheetTop(top)
      setIsAnimating(animate && !prefersReducedMotion)
    },
    [getContainerH, prefersReducedMotion]
  )

  const measureAndSnap = useCallback(
    (animate = false) => {
      const top = measureContentTop()
      topOffsetRef.current = top
      setTopOffset(top)
      snapToIndex(detentIndexRef.current, animate)
    },
    [snapToIndex]
  )

  // Measure synchronously before first paint — no flash.
  useLayoutEffect(() => {
    measureAndSnap(false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('resize', measureAndSnap)
    return () => window.removeEventListener('resize', measureAndSnap)
  }, [measureAndSnap])

  // Prevent the page from scrolling into the blank area below the fixed overlay.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const handleTransitionEnd = useCallback(() => {
    setIsAnimating(false)
  }, [])

  const handleAutoRaise = useCallback(() => {
    if (detentIndexRef.current === 0) snapToIndex(1)
  }, [snapToIndex])

  const wrappedOnTabChange = useCallback(
    (tab) => {
      onTabChange?.(tab)
      handleAutoRaise()
    },
    [onTabChange, handleAutoRaise]
  )

  // ── Pointer events ─────────────────────────────────────────────────────────

  const handlePointerDown = useCallback((e) => {
    draggingRef.current = true
    movedRef.current = 0
    startYRef.current = e.clientY
    startTopRef.current = currentTopRef.current
    setIsAnimating(false)
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback(
    (e) => {
      if (!draggingRef.current) return
      const dy = e.clientY - startYRef.current
      movedRef.current = Math.abs(dy)
      const containerH = getContainerH()
      const tops = getDetentTops(containerH)
      const effectiveNavH = actionsRef.current ? NAV_H : 0
      const min = tops[DETENTS.length - 1]
      // Hard cap: grabber must always stay on screen
      const max = containerH - effectiveNavH - 28
      const nextTop = Math.min(Math.max(startTopRef.current + dy, min), max)
      currentTopRef.current = nextTop
      if (sheetRef.current) {
        sheetRef.current.style.top = `${nextTop}px`
      }
    },
    [getContainerH]
  )

  const handlePointerUp = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false

    if (movedRef.current < 6) {
      snapToIndex((detentIndexRef.current + 1) % DETENTS.length)
      return
    }

    const tops = getDetentTops(getContainerH())
    const t = currentTopRef.current
    let best = 0
    let bestDist = Infinity
    tops.forEach((v, i) => {
      const d = Math.abs(v - t)
      if (d < bestDist) {
        bestDist = d
        best = i
      }
    })
    snapToIndex(best)
  }, [snapToIndex, getContainerH])

  const handlePointerCancel = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false
    snapToIndex(detentIndexRef.current)
  }, [snapToIndex])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {topOffset !== null && (
        <div className={S.root} style={{ top: topOffset }}>

          {topStrip && (
            <div className={S.sizeStrip}>{topStrip}</div>
          )}

          <div
            className={[S.preview, !topStrip && S.previewNoStrip].filter(Boolean).join(' ')}
            style={{ bottom: actions ? NAV_H : 0 }}
          >
            {mainContent}
            {showFullscreenBtn && (
              <button
                type="button"
                className={S.fullscreenBtn}
                onClick={() => setPreviewFullscreen(true)}
                aria-label="Expand preview"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            )}
          </div>

          {/* Extended drag zone — lets users drag the sheet from up to 60px into the preview area */}
          {sheetTop > 0 && (
            <div
              className={S.extendedDragZone}
              style={{
                top: Math.max(0, sheetTop - 60),
                height: Math.min(60, sheetTop),
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
            />
          )}

          <div
            ref={sheetRef}
            className={S.sheet}
            style={{
              top: sheetTop,
              bottom: actions ? NAV_H : 0,
              transition: isAnimating
                ? 'top 0.3s cubic-bezier(.32,.72,0,1)'
                : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            <div
              className={S.grabber}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
            >
              <span className={S.grabberBar} />
            </div>

            {tabsOverride !== undefined ? (
              tabsOverride != null && <div className={S.tabs}>{tabsOverride}</div>
            ) : (
              tabs && (
                <div className={S.tabs}>
                  <SubtabNav
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={wrappedOnTabChange}
                  />
                </div>
              )
            )}

            <div className={S.scroll} onPointerDownCapture={handleAutoRaise}>
              {sheetContent}
            </div>
          </div>

          {actions && (
            <div className={S.nav}>
              {actions}
            </div>
          )}

        </div>
      )}

      {previewFullscreen && (
        <div
          className="workflow-preview-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label="Full screen preview"
          onClick={() => setPreviewFullscreen(false)}
        >
          <div className="workflow-preview-fullscreen__header">
            <button
              type="button"
              className="workflow-preview-fullscreen__close"
              onClick={() => setPreviewFullscreen(false)}
              aria-label="Close full screen preview"
            >
              ×
            </button>
          </div>
          <div
            className="workflow-preview-fullscreen__body"
          >
            {mainContent}
          </div>
        </div>
      )}
    </>
  )
}
